import {
  ActionButton,
  ActionButtonOption,
  Badge,
  BadgeVariantEnum,
  FontSizeEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  PauseIsolatedIcon,
  PlayIsolatedIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TFeaturedPeopleType } from '@/features/featured-people/types';
import { PresenceTypeEnum } from '@/types/presences';
import { EVisibility } from '@/types/types';
import { formatHMS } from '@/utils/time';

import { UNDEFINED_SUPPORT_ID } from '../constants';
import { useTimeKeepingContext } from '../context/TimeKeepingContext';
import { useResetConfirmation } from '../hooks/use-reset-confirmation';
import { ItemLayoutType, TimeKeepingButtonStateEnum } from '../types';
import { buildCompositeKey } from '../utils/composite-key';
import { computeRecordsDuration } from '../utils/compute-records-duration';

import { DEFAULT_BUTTON_STATE, TimeKeepingButtonState } from './TimeKeepingAccordion';
import { TimeKeepingResetModal } from './TimeKeepingResetModal';
import { TimeKeepingSupportRecord } from './TimeKeepingSupportRecord';

type TimeKeepingPeopleCardProps = {
  item: ItemLayoutType;
  /** Resolved by the dashboard for every card; a card never asks for its own. */
  person?: TFeaturedPeopleType;
  z: number;
  onBringToTop: (compositeId: string) => void;
  onOpenVisibility: (
    compositeId: string,
    supportPresenceId: string,
    recordIndex: number,
    currentVisibility: EVisibility,
    el: HTMLElement
  ) => void;
};

export function TimeKeepingPeopleCard({
  item,
  person,
  z,
  onBringToTop,
  onOpenVisibility,
}: TimeKeepingPeopleCardProps) {
  const { t } = useTranslation();
  const {
    onRemoveItem,
    onResetRecords,
    onAddRecord,
    onUpdateRecord,
    onRemoveRecord,
    buildNewRecord,
  } = useTimeKeepingContext();
  const { playerVideoCurrentTime, playerVideoIsPlaying, getDateTimeCurrentPresence } =
    useBroadcastScheduleProgram();
  const compositeId = buildCompositeKey(PresenceTypeEnum.PEOPLE, item.presence_id);
  const imageSrc = person?.parameters?.main_logo_presigned_url ?? '';
  const displayName = person?.name ?? item.presence_id;

  const [buttonState, setButtonState] = useState<TimeKeepingButtonState>(DEFAULT_BUTTON_STATE);

  const records = item.records ?? [];
  const isButtonActive = buttonState.state === TimeKeepingButtonStateEnum.ACTIVE;

  let totalSeconds = computeRecordsDuration(records);
  if (isButtonActive && buttonState.recordIndex !== undefined) {
    const activeRec = records[buttonState.recordIndex];
    if (activeRec && activeRec.datetimeEnd === '0') {
      const now = getDateTimeCurrentPresence(playerVideoCurrentTime) ?? new Date();
      const diff = now.getTime() - new Date(activeRec.datetimeStart).getTime();
      if (diff > 0) totalSeconds += diff / 1000;
    }
  }
  const totalTime = formatHMS(totalSeconds);

  const getButtonColor = () => {
    if (buttonState.state === TimeKeepingButtonStateEnum.INACTIVE)
      return IconButtonColorEnum.DEFAULT;
    return playerVideoIsPlaying ? IconButtonColorEnum.SECONDARY : IconButtonColorEnum.PRIMARY;
  };

  const handlePlayPause = useCallback(() => {
    if (buttonState.state === TimeKeepingButtonStateEnum.INACTIVE) {
      onAddRecord(compositeId, UNDEFINED_SUPPORT_ID, buildNewRecord());
      setButtonState({ state: TimeKeepingButtonStateEnum.ACTIVE, recordIndex: records.length });
      return;
    }

    if (buttonState.recordIndex === undefined) {
      setButtonState(DEFAULT_BUTTON_STATE);
      return;
    }

    const activeRecord = records[buttonState.recordIndex];
    if (!activeRecord) {
      setButtonState(DEFAULT_BUTTON_STATE);
      return;
    }

    const endDate = getDateTimeCurrentPresence(playerVideoCurrentTime) ?? new Date();
    const startDate = new Date(activeRecord.datetimeStart);
    onUpdateRecord(compositeId, UNDEFINED_SUPPORT_ID, buttonState.recordIndex, {
      datetimeEnd: endDate < startDate ? startDate : endDate,
    });
    setButtonState(DEFAULT_BUTTON_STATE);
  }, [
    buttonState,
    records,
    compositeId,
    onAddRecord,
    onUpdateRecord,
    buildNewRecord,
    getDateTimeCurrentPresence,
    playerVideoCurrentTime,
  ]);

  const executeResetAll = useCallback(() => {
    onResetRecords(compositeId);
    setButtonState(DEFAULT_BUTTON_STATE);
  }, [compositeId, onResetRecords]);

  const { pendingReset, requestResetAll, cancelReset } = useResetConfirmation();

  const handleConfirmReset = useCallback(() => {
    executeResetAll();
    cancelReset();
  }, [executeResetAll, cancelReset]);

  const cardOptions: ActionButtonOption[] = [
    { label: t('Reiniciar tiempos'), action: () => requestResetAll(compositeId) },
    { label: t('Ocultar'), action: () => onRemoveItem(compositeId) },
  ];

  return (
    <div
      key={compositeId}
      data-id="card-widget"
      className="relative flex h-full flex-col overflow-hidden rounded-md border border-neutral-15 bg-white shadow-lg"
      style={{ zIndex: z }}
      onPointerDown={() => onBringToTop(compositeId)}
    >
      <div
        data-id="card-widget-header"
        className="card__widget__header cursor-move bg-extra-80/10 p-1"
      >
        <div data-id="card-widget-presence-resume" className="flex items-center justify-between">
          <img
            data-id="card-widget-presence-image"
            alt={displayName}
            src={imageSrc}
            className="h-10 w-10 object-contain"
          />
          <div className="z-500 flex items-center gap-2">
            <Typography fontSize={FontSizeEnum.Sm}>{totalTime}</Typography>
            <Tooltip help={t('Opciones')} grow>
              <ActionButton rounded={true} options={cardOptions} />
            </Tooltip>
          </div>
        </div>
        <div className="overflow-hidden text-ellipsis whitespace-nowrap pb-1 pt-2 text-[12px]">
          {displayName}
        </div>
        <div className="flex items-center justify-between pr-1 pt-1">
          <IconButton
            color={getButtonColor()}
            size={IconButtonSizeEnum.XS}
            onClick={handlePlayPause}
            onPointerDown={e => e.stopPropagation()}
            className="mt-0.5 min-h-4 min-w-4 shrink-0"
          >
            <PlayIsolatedIcon
              size={IconSizeEnum['2XS']}
              className={clsx(isButtonActive && 'hidden')}
            />
            <PauseIsolatedIcon
              size={IconSizeEnum['2XS']}
              className={clsx(!isButtonActive && 'hidden')}
            />
          </IconButton>
          <Badge data-id="people-records" variant={BadgeVariantEnum.DarkFilled}>
            {records.length}
          </Badge>
        </div>
      </div>

      <div data-id="card-widget-content" className="min-h-0 flex-1 overflow-y-auto">
        <ol data-id="card-widget-supports-table" className="text-[13px]">
          {records.map((record, idx) => {
            const isActive =
              buttonState.state === TimeKeepingButtonStateEnum.ACTIVE &&
              buttonState.recordIndex === idx;
            return (
              <li key={idx} className="border-b border-dotted border-gray-200 bg-bg-primary-10">
                <TimeKeepingSupportRecord
                  compositeId={compositeId}
                  supportPresenceId={UNDEFINED_SUPPORT_ID}
                  record={record}
                  idx={idx}
                  isActive={isActive}
                  playerVideoCurrentTime={playerVideoCurrentTime}
                  getDateTimeCurrentPresence={getDateTimeCurrentPresence}
                  onRemove={onRemoveRecord}
                  onToggleVisibility={onOpenVisibility}
                />
              </li>
            );
          })}
        </ol>
      </div>

      <TimeKeepingResetModal
        isOpen={pendingReset !== null}
        scope="all"
        onClose={cancelReset}
        onConfirm={handleConfirmReset}
      />
    </div>
  );
}
